import { CommonModule, Location } from '@angular/common';
import { Component, EnvironmentInjector, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { catchError, mergeMap, of, tap } from 'rxjs';
import { InsertOrderItemRequest, InsertOrderRequest, OrderResponse, ServicesPaymentsApiService } from 'src/app/core/services/payments-api-service';
import { HeaderService } from 'src/app/services/header.service';
import { UnikoServicesConfig, UnikoServiceTypes } from 'src/app/services/payments-api.service';
import { ArticleStatus, AuctionDetails, AuctionRound, PortalApiService, PropertyDetails, PropertySummaryWithCounts } from 'src/app/services/portal-api.service';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import { CaseTypeCodes } from 'src/app/services/workflow.service';
import { FilterDropdownPropertyItem, FilterPropertyDropdownComponent } from 'src/app/shared/components/filter-property-dropdown/filter-property-dropdown.component';
import { NavigationButtonsComponent } from 'src/app/shared/components/navigation-buttons/navigation-buttons.component';
import { TabPillGroupButton, TabPillGroupButtonComponent } from 'src/app/shared/components/tab-pill-group-button/tab-pill-group-button.component';
import { CustomTooltipDirective } from 'src/app/shared/directives/custom-tooltip.directive';
import { ProgressStepperComponent } from "../../../shared/components/progress-stepper/progress-stepper.component";
import { createStepMapFromStringArray, defaultCheckoutStepsArray, navigateToCheckout } from 'src/app/shared/components/checkout/utils';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { LoaderComponent } from "../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-service-property-selector',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoDirective,
    FilterPropertyDropdownComponent,
    TabPillGroupButtonComponent,
    NavigationButtonsComponent,
    CustomTooltipDirective,
    ProgressStepperComponent,
    LoaderComponent
  ],
  templateUrl: './service-property-selector.component.html',
  styleUrl: './service-property-selector.component.css'
})
export class ServicePropertySelectorComponent implements OnInit {

  protected propertyFavourites: FilterDropdownPropertyItem[] = [];
  protected myProperties: FilterDropdownPropertyItem[] = [];
  protected showMyProperties: boolean = true;
  protected selectedProperty: PropertyDetails = new PropertyDetails();
  protected serviceType: UnikoServiceTypes;
  protected caseItem: string | null = null;
  protected altHeaderTitle: string = 'Select Property';
  protected tabPillGroupButtons: TabPillGroupButton[] = [];
  private caseTypeCode: string | undefined;
  public showOnlyMineProperties = false;
  public onlyMinePropertiesServices = [UnikoServiceTypes.preapproval, UnikoServiceTypes.photography, UnikoServiceTypes.propertyFileConstitution];
  private noPropertySelectedError!: string;
  protected Steps: string[] = []
  protected isLoading: boolean = false;

  constructor(
    private _headerService: HeaderService,
    private _portalService: PortalApiService,
    private _location: Location,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _paymentsApiService: ServicesPaymentsApiService,
    private _translate: TranslocoService,
    @Inject(ToasterService) private _toastService: ToasterService,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector,
  ) {

    const serviceTypeParam = this._activatedRoute.snapshot.paramMap.get('serviceType');
    this.serviceType = UnikoServiceTypes[serviceTypeParam as keyof typeof UnikoServiceTypes];
    this.showOnlyMineProperties = this.onlyMinePropertiesServices.includes(this.serviceType);
    this.caseTypeCode = CaseTypeCodes[serviceTypeParam as keyof typeof CaseTypeCodes];

    if (!this.serviceType) {
      this._location.back();
      console.error('service type not found');
    }

    _translate.selectTranslateObject('services.property-selector').pipe(
      tap(t => {
        this.applyTranslations(t);
      })
    ).subscribe();
  }

  ngOnInit(): void {
    this.Steps = ['service-purchase.select-listing'].concat(defaultCheckoutStepsArray());
    this._headerService.setAltNavigation(this._translate.translate('services.property-selector.altheader.selectProperty'), undefined)
    console.log({ service: this.serviceType })
    this.showMyProperties = this.serviceType != UnikoServiceTypes.biddingSupport;
    this.isLoading = true;
    this._portalService.getMySellingProperties(this.caseTypeCode).pipe(
      tap(mySellingProperties => {
        if (mySellingProperties) {
          mySellingProperties.items?.filter(property => property.publishedStatus == ArticleStatus.Published || ['photography', 'file-constitution'].includes(this.caseTypeCode ?? ''))?.map((property: PropertySummaryWithCounts) => { //Photography should also show unpublished properties
            this.myProperties?.push({
              key: property.id,
              title: `${(property.type !== 'Unknown' ? property.type : '') ?? ''}, ${property.propertyArea ?? ''}`.trim(),
              address: `${property.address?.streetName ?? ''} ${property.address?.prefecture ?? ''}`.trim(),
              floor: property.floor,
              bedrooms: property.numberOfRooms,
              bathrooms: property.numberOfBathrooms,
              constructionYear: property.constructionYear,
              auctions: property.auctionRounds,
              defaultPhoto: (property.defaultPhoto?.photoItems && property.defaultPhoto?.photoItems.find(img => img.uri && img.uri.includes('s.webp'))?.uri) ?? property.defaultImagePath
            } as FilterDropdownPropertyItem)
          })
          if (this.showMyProperties) {
            this.tabPillGroupButtons.push({
              key: 'my-listings',
              title: 'Οι καταχωρίσεις μου',
              icon: 'icon-house',
            })
          }
        }
      }),
      mergeMap(() => {
        if (this.showOnlyMineProperties) {
          return of(null);
        }
        return this._portalService.getMyFavorites(1, 100).pipe(
          catchError((error) => {
            return of(null);
          })
        )
      }),
      tap(favorites => {
        if (favorites) {
          favorites.items?.map(property => {
            this.propertyFavourites?.push({
              key: property.id,
              title: `${property.type ?? ''}, ${property.propertyArea ?? ''}`.trim(),
              address: `${property.address?.streetName ?? ''} ${property.address?.prefecture ?? ''}`.trim(),
              floor: property.floor,
              bedrooms: property.numberOfRooms,
              bathrooms: property.numberOfBathrooms,
              constructionYear: property.constructionYear,
              auctions: property.auctionRounds,
              defaultPhoto: property.defaultPhoto?.photoItems?.[0]?.uri ?? property.defaultImagePath
            } as FilterDropdownPropertyItem)
          })

          this.tabPillGroupButtons.push({
            key: 'favorites',
            title: 'Αγαπημένα',
            icon: 'icon-heart-line',
          })

        }

      })
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (!this.showOnlyMineProperties) {
          this.tabPillGroupButtons.push({
            key: 'enter-code',
            title: 'Βάλε κωδικό',
            icon: 'icon-edit',
          })
        }
      },
    });
  }

  applyTranslations(t: any) {
    let myListings = this.tabPillGroupButtons.find(f => f.key.includes('my-listings'));
    let favorites = this.tabPillGroupButtons.find(f => f.key.includes('favorites'));
    let byCode = this.tabPillGroupButtons.find(f => f.key.includes('enter-code'));

    if (myListings) myListings.title = t['tab.myListings'].title;
    if (favorites) favorites.title = t['tab.favorites'].title;
    if (byCode) byCode.title = t['tab.byCode'].title;
    this.altHeaderTitle = t['altheader'].title;
    this.noPropertySelectedError = t['infoText'];
  }

  handleButtonGroupItemSelected(item: any) {
    this.caseItem = item;
  }

  handlePropertySelection(property: { id: string, auctionDetails?: AuctionDetails }) {
    this.selectedProperty.id = property.id;
    this.selectedProperty.auctionRounds = property?.auctionDetails;
  }

  private biddingSupportIsValid(property: PropertyDetails): boolean {
    if (this.serviceType !== UnikoServiceTypes.biddingSupport) return true;

    if (!property.auctionRounds || property?.auctionRounds?.rounds?.length === 0) {
      this.showErrorToast(
        this._translate.translate("This property is not eligible for this service."),
        this._translate.translate("To use this service, the property must currently be listed for auction.")
      );
      return false;
    }

    const activeRounds = property?.auctionRounds?.rounds?.filter(
      (x: AuctionRound) => x.roundDate && new Date(x.roundDate) > new Date()
    );

    if (!activeRounds || activeRounds.length === 0) {
      this.showErrorToast(
        this._translate.translate("This property is not eligible for this service."),
        this._translate.translate("To use this service, the property must have an active auction round.")
      );
      return false;
    }

    const soonestActiveRound = activeRounds.reduce((earliest: AuctionRound, current: AuctionRound) => {
      const earliestDate = new Date(earliest.roundDate!);
      const currentDate = new Date(current.roundDate!);
      return currentDate < earliestDate ? current : earliest;
    });

    const soonestAuctionDate = new Date(soonestActiveRound.roundDate!);
    const diffInDays = (soonestAuctionDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);

    if (diffInDays <= 10) {
      this.showErrorToast(
        this._translate.translate("This property is not eligible for this service."),
        this._translate.translate("To use this service, the next auction round is taking place in less than 10 days.")
      );
      return false;
    }

    return true;
  }

  private showErrorToast(title: string, message: string): void {
    this._toastService.showBanner(ToastType.Error, title, message);
  }

  protected proceedToCheckout(): void {
    const sku = UnikoServicesConfig.find(f => f.type == this.serviceType)?.sku;
    if (sku && this.selectedProperty) {
      let orderItems: InsertOrderItemRequest[] = [];

      orderItems.push(new InsertOrderItemRequest({
        sku: sku,
        quantity: 1,
        propertyId: this.selectedProperty.id,
      }));

      let body = new InsertOrderRequest({
        currency: 'EUR',
        orderItems: orderItems,
      });

      if (!this.biddingSupportIsValid(this.selectedProperty)) {
        return;
      }

      this._paymentsApiService.createMyOrder(body).pipe(
        catchError((err: any) => {
          this._toastService.showBanner(ToastType.Error, this._translate.translate('Property id is not valid, please try again'), undefined, 5000);

          return of();
        }),
        tap((response: OrderResponse) => {
          if (response && response.number) {
            const map = createStepMapFromStringArray(this.Steps, -1)
            navigateToCheckout(this._router, `/checkout/${response.number}`, map)
          }
        })
      ).subscribe();
    } else {
      this._toastService.showBanner(ToastType.Error, this.noPropertySelectedError, undefined, 5000);
    }
  }

  protected handleTypedPropertyId(event: Event) {
    const target = event.target as HTMLInputElement;
    this.selectedProperty.id = target.value;
    this.propertyCodeToID();
  }

  protected propertyCodeToID() {
    this._portalService.getPropertyById(this.selectedProperty?.id as string).subscribe({
      next: (res) => {
        if (res?.id && res.publishedStatus == ArticleStatus.Published) {
          this.selectedProperty = res;
        } else {
          this.selectedProperty.id = ''
        }
      },
      error: (err) => {
        this.selectedProperty.id = '';
        this._toastService.showBanner(ToastType.Error, this._translate.translate('Property id is not valid, please try again'), undefined, 5000);
      },
    });
  }

  protected navigationBack(): void {
    this._location.back();
  }

}
